import React, {useState} from "react";
import {emitNow} from "../utils/clientSocket";
import CustomRange from "./CustomRange";
import {TimeVariants} from "../utils/gameEnums";
import Modal from "./ui/Modal";
import Icon from "./ui/Icon";

export const TIME_PRESETS = [
    {id: 1, variant: TimeVariants.BULLET, minutes: 1, increment: 0},
    {id: 2, variant: TimeVariants.BULLET, minutes: 1, increment: 2},
    {id: 3, variant: TimeVariants.BULLET, minutes: 2, increment: 1},
    {id: 4, variant: TimeVariants.BLITZ, minutes: 3, increment: 2},
    {id: 5, variant: TimeVariants.BLITZ, minutes: 5, increment: 0},
    {id: 6, variant: TimeVariants.BLITZ, minutes: 5, increment: 5},
    {id: 7, variant: TimeVariants.RAPID, minutes: 10, increment: 0},
    {id: 8, variant: TimeVariants.RAPID, minutes: 10, increment: 5},
    {id: 9, variant: TimeVariants.RAPID, minutes: 15, increment: 10},
    {id: 10, variant: TimeVariants.CLASSIC, minutes: 60, increment: 15},
    {id: 11, variant: TimeVariants.CLASSIC, minutes: 90, increment: 30},
    {id: 12, variant: TimeVariants.CLASSIC, minutes: 120, increment: 60}
];

export const STAGE_OPTIONS = [
    {id: 1, imageSrc: "/sunrise.png", title: "Early game", range: [10, 20]},
    {id: 2, imageSrc: "/midday.png", title: "Mid game", range: [20, 40]},
    {id: 3, imageSrc: "/nighttime.png", title: "Late game", range: [40, 100]}
];

export const HANDICAP_OPTIONS = [
    {id: 1, imageSrc: "/hiking.png", title: "Almost even", range: [0, 1]},
    {id: 2, imageSrc: "/stairs.png", title: "Uphill battle", range: [1, 3]},
    {id: 3, imageSrc: "/mountain.png", title: "Free climbing", range: [3, 5]}
];

export const OptionTile = ({selected, onClick, imageSrc, title, value}) => (
    <button type="button" className={`option-tile ${selected ? "selected" : ""}`} onClick={onClick}
            aria-pressed={selected}>
        {imageSrc && <img src={imageSrc} alt=""/>}
        <span className="option-tile__value">{value}</span>
        <span className="option-tile__title">{title}</span>
    </button>
);

const CreateGameModal = ({showCreateModal, closeModal, clubId, friendName}) => {
    const [presetId, setPresetId] = useState(5);
    const [isCustomTime, setIsCustomTime] = useState(false)
    const [customMinutes, setCustomMinutes] = useState(5);
    const [customIncrement, setCustomIncrement] = useState(3);
    const [selectedVariant, setSelectedVariant] = useState("standard");
    const [selectedColor, setSelectedColor] = useState("random");
    const [stageId, setStageId] = useState(2);
    const [handicapId, setHandicapId] = useState(1);
    const [isCustomJumpstart, setIsCustomJumpstart] = useState(false)
    const [evalRange, setEvalRange] = useState([0, 5]);
    const [moveRange, setMoveRange] = useState([10, 100]);
    const [isCreatorsHandicap, setIsCreatorsHandicap] = useState(false)
    const [error, setError] = useState(null)

    if (!showCreateModal) return null;

    const preset = TIME_PRESETS.find((p) => p.id === presetId);
    const timeMinutes = isCustomTime ? customMinutes : preset?.minutes;
    const incrementSeconds = isCustomTime ? customIncrement : preset?.increment;

    const startGame = () => {
        if (!timeMinutes && !incrementSeconds) {
            setError("Choose a time control first.");
            return;
        }

        const gameOffer = {
            baseTime: timeMinutes,
            increment: incrementSeconds,
            variant: selectedVariant,
            clubId,
            selectedColor,
            isCreatorsHandicap
        };

        if (selectedVariant === "jumpstart") {
            const evals = isCustomJumpstart ? evalRange : HANDICAP_OPTIONS.find((o) => o.id === handicapId).range;
            const stage = isCustomJumpstart ? moveRange : STAGE_OPTIONS.find((o) => o.id === stageId).range;
            gameOffer.lower_eval_limit = evals[0];
            gameOffer.upper_eval_limit = evals[1];
            gameOffer.lower_move_limit = stage[0];
            gameOffer.upper_move_limit = stage[1];
        }

        if (friendName) gameOffer.friendName = friendName;
        if (!emitNow(friendName ? "createFriendChallenge" : "createGameOffer", gameOffer)) {
            setError("You are offline right now. Wait until the connection is back, then try again.");
            return;
        }

        setError(null);
        closeModal();
    };

    return (
        <Modal
            onClose={closeModal}
            title={friendName ? `Challenge ${friendName}` : "Create a game"}
            labelledBy="create-game-title"
            footer={<>
                <button className="btn" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={startGame}>
                    <Icon name={friendName ? "swords" : "board"}/>
                    {friendName ? "Send challenge" : "Create game"}
                </button>
            </>}
        >
            <div className="modal-body">
                <div className="option-group">
                    <div className="option-group__label">Variant</div>
                    <div className="segmented" style={{width: "100%"}}>
                        {[["standard", "Standard"], ["jumpstart", "Jumpstart"]].map(([value, label]) => (
                            <button key={value} type="button" className={selectedVariant === value ? "active" : ""}
                                    style={{flex: 1, justifyContent: "center"}}
                                    onClick={() => setSelectedVariant(value)}>{label}</button>
                        ))}
                    </div>
                    {selectedVariant === "jumpstart" && (
                        <p className="small muted" style={{marginTop: "0.4rem"}}>
                            Jumpstart drops you into a random position from a real game, where one side starts with an advantage.
                        </p>
                    )}
                </div>

                <div className="option-group">
                    <div className="option-group__label">
                        <span>Time control</span>
                        <button type="button" className="link-btn" onClick={() => setIsCustomTime(!isCustomTime)}>
                            {isCustomTime ? "Show presets" : "Custom time"}
                        </button>
                    </div>
                    {isCustomTime ? (
                        <div className="stack stack--sm">
                            <div className="field">
                                <label htmlFor="minutes-slider">Minutes per player: <strong>{customMinutes}</strong></label>
                                <input id="minutes-slider" className="slider" type="range" min="0.5" max="60" step="0.5"
                                       value={customMinutes} onChange={(e) => setCustomMinutes(Number(e.target.value))}/>
                            </div>
                            <div className="field">
                                <label htmlFor="increment-slider">Increment in seconds: <strong>{customIncrement}</strong></label>
                                <input id="increment-slider" className="slider" type="range" min="0" max="30" step="1"
                                       value={customIncrement} onChange={(e) => setCustomIncrement(Number(e.target.value))}/>
                            </div>
                        </div>
                    ) : (
                        <div className="option-grid">
                            {TIME_PRESETS.map((option) => (
                                <OptionTile key={option.id} selected={presetId === option.id}
                                            onClick={() => setPresetId(option.id)}
                                            imageSrc={option.variant.imageSrc}
                                            title={option.variant.title}
                                            value={`${option.minutes} + ${option.increment}`}/>
                            ))}
                        </div>
                    )}
                </div>

                {selectedVariant === "standard" ? (
                    <div className="option-group">
                        <div className="option-group__label">Play as</div>
                        <div className="color-choice">
                            {[["white", "White"], ["random", "Random"], ["black", "Black"]].map(([value, label]) => (
                                <button key={value} type="button" className={selectedColor === value ? "selected" : ""}
                                        onClick={() => setSelectedColor(value)} aria-pressed={selectedColor === value}>
                                    <span className={`color-pip color-pip--${value}`}/>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="option-group">
                            <div className="option-group__label">
                                <span>{isCustomJumpstart ? "Custom position" : "Game stage"}</span>
                                <button type="button" className="link-btn"
                                        onClick={() => setIsCustomJumpstart(!isCustomJumpstart)}>
                                    {isCustomJumpstart ? "Show presets" : "Fine tune"}
                                </button>
                            </div>
                            {isCustomJumpstart ? (
                                <div className="stack">
                                    <CustomRange title="Advantage (pawns)" min={0} max={5} step={0.2}
                                                 initial={evalRange} onChange={setEvalRange}
                                                 format={(v) => Number(v).toFixed(1)}/>
                                    <CustomRange title="Moves played" min={10} max={100} step={1}
                                                 initial={moveRange} onChange={setMoveRange}/>
                                </div>
                            ) : (
                                <div className="option-grid" style={{gridTemplateColumns: "repeat(3, 1fr)"}}>
                                    {STAGE_OPTIONS.map((option) => (
                                        <OptionTile key={option.id} selected={stageId === option.id}
                                                    onClick={() => setStageId(option.id)} imageSrc={option.imageSrc}
                                                    title={`moves ${option.range[0]}-${option.range[1]}`}
                                                    value={option.title}/>
                                    ))}
                                </div>
                            )}
                        </div>
                        {!isCustomJumpstart && (
                            <div className="option-group">
                                <div className="option-group__label">Handicap</div>
                                <div className="option-grid" style={{gridTemplateColumns: "repeat(3, 1fr)"}}>
                                    {HANDICAP_OPTIONS.map((option) => (
                                        <OptionTile key={option.id} selected={handicapId === option.id}
                                                    onClick={() => setHandicapId(option.id)} imageSrc={option.imageSrc}
                                                    title={`${option.range[0]}-${option.range[1]} pawns`}
                                                    value={option.title}/>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="option-group">
                            <label className="check-row">
                                <input type="checkbox" checked={isCreatorsHandicap}
                                       onChange={() => setIsCreatorsHandicap((prev) => !prev)}/>
                                I want to start with the advantage
                            </label>
                        </div>
                    </>
                )}

                {error && <div className="form-error" style={{marginTop: "1rem"}}>{error}</div>}
            </div>
        </Modal>
    );
};

export default CreateGameModal;
